/* -------------------------------------------------------------------------- */
/*  CalendarView.tsx                                                          */
/* -------------------------------------------------------------------------- */
"use client";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Specialist, Appointment } from "@/lib/types";

import { AppointmentCard } from "@/components/appointment-card";
import { BusyBlocksLayer } from "@/components/busy-blocks-layer";
import { EmployeeHeader } from "@/layouts/employee-header";
import { WeekdayHeader } from "@/components/custom/WeekdayHeader";
import { ModalCreateAppointment } from "@/components/custom/appointment-modal-create/appointment-modal-create";

import {
  AppointmentCreateModalProvider,
  useAppointmentCreateModal,
} from "@/contexts/AppointmentCreateModalContext";
import {
  CalendarDragProvider,
  ColumnDragGhost,
  useCalendarDragActions,
  useDraggableAppointment,
  useDropColumn,
  usePagerDropZone,
} from "@/contexts/CalendarDragContext";
import { LinkedAppointmentsProvider } from "@/contexts/LinkedAppointmentsContext";
import { useCalendar } from "@/contexts/CalendarContext";
import { useCalendarData } from "@/contexts/CalendarDataProvider";
import { useSpecialistsForDate } from "@/hooks/calendar/useSpecialistsForDate";
import { useWeekAppointments } from "@/hooks/calendar/useWeekAppointments";
import { useWeekAppointmentsForStaff } from "@/hooks/calendar/useWeekAppointmentsForStaff";
import { useWeekSchedule } from "@/hooks/calendar/useWeekSchedule";
import { useExternalBusyWeek } from "@/hooks/calendar/useExternalBusy";
import { getWeekStart } from "@/lib/utils"
import { layoutAppointments } from "@/lib/calendar/layoutAppointments";
import { busyColumnKey, groupBusyBlocks, type BusyBlock } from "@/lib/calendar/externalBusy";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";
import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { useAccessPolicy } from "@/hooks/access/useAccess";
import { earliestAllowedDate } from "@/lib/auth/dateLimit";
import { useIsMobile } from "@/hooks/use-mobile";
// Time handling must follow .clinerules/time-handling.md
// Use salonNow()/fmtSalonHM (Luxon DateTime in salon zone), never device time.
// Ref: .clinerules/time-handling.md
import { salonNow, fmtSalonHM, minutesOfDayFromIsoSalon } from "@/lib/time";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { goToDashboard } from "@/features/sso/goToDashboard";

/* -------------------------------------------------------------------------- */
/*                                   utils                                    */
/* -------------------------------------------------------------------------- */

import { DateTime } from "luxon";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";

/** Returns ISO-date strings for Monday–Sunday of the week containing `date`. */
const getWeekDates = (date: DateTime): string[] => {
  // Find Monday of the week
  const monday = date.minus({ days: (date.weekday + 6) % 7 });
  return [...Array(7)].map((_, i) => monday.plus({ days: i }).toISODate()!);
};

// Helper: get all dates in the week starting at weekStart (YYYY-MM-DD)
function getWeekDates2(weekStart: string): string[] {
  const dates: string[] = [];
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  const start = DateTime.fromISO(weekStart, { zone: tz });
  for (let i = 0; i < 7; i++) {
    dates.push(start.plus({ days: i }).toISODate()!);
  }
  return dates;
}

/** Default calendar start hour (fallback when no master works earlier) */
const DEFAULT_START_HOUR = 9;
/** Minimum end hour (fallback when no master works later) */
const DEFAULT_END_HOUR = 21;


/**
 * Scan all specialists' intervals (seconds) and return the highest end-hour
 * (ceiling), but never lower than DEFAULT_END_HOUR.
 */
function getMaxEndHour(specialists: { intervals?: { start: number; end: number }[] }[]): number {
  let maxSec = 0;
  for (const s of specialists) {
    for (const iv of s.intervals ?? []) {
      if (iv.end > maxSec) maxSec = iv.end;
    }
  }
  if (maxSec <= 0) return DEFAULT_END_HOUR;
  return Math.max(DEFAULT_END_HOUR, Math.ceil(maxSec / 3600));
}

/**
 * Scan all specialists' intervals (seconds) and return the lowest start-hour
 * (floor), but never higher than DEFAULT_START_HOUR.
 */
function getMinStartHour(specialists: { intervals?: { start: number; end: number }[] }[]): number {
  let minSec = Infinity;
  for (const s of specialists) {
    for (const iv of s.intervals ?? []) {
      if (iv.start < minSec) minSec = iv.start;
    }
  }
  if (!Number.isFinite(minSec) || minSec < 0) return DEFAULT_START_HOUR;
  return Math.min(DEFAULT_START_HOUR, Math.floor(minSec / 3600));
}

/** Build the array of hour-slot starts for a given start/end hour range */
function buildHours(startHour: number, endHour: number): number[] {
  return Array.from({ length: endHour - startHour }, (_, i) => i + startHour);
}

// Geometry constants for vertical sizing
function getHourPx(): number {
  if (typeof window === "undefined") return 70;
  const v = getComputedStyle(document.documentElement).getPropertyValue("--slot-h")?.trim();
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 70;
}
const pad = (n: number) => n.toString().padStart(2, "0");
const minutesToHM = (mins: number) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
const addMinutes = (mins: number, add: number) => mins + add;

// How many masters per desktop "page"
const DESKTOP_PAGE_SIZE = 8;

/** A column with no busy time held outside Boona. */
const NO_BUSY: readonly BusyBlock[] = [];

function timeToMinutes(t: string) {
  if (!t) return NaN;
  const m = t.trim().match(/^(\d{1,2})(?::|\.|$)?(\d{2})?$/);
  if (!m) return NaN;
  const h = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  const mm = m[2] ? Math.max(0, Math.min(59, parseInt(m[2], 10))) : 0;
  return h * 60 + mm;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

/* -------------------------------------------------------------------------- */
/*                              exported wrapper                              */
/* -------------------------------------------------------------------------- */

export const CalendarView = () => (
  <AppointmentCreateModalProvider>
    <LinkedAppointmentsProvider>
      <CalendarViewInner />
    </LinkedAppointmentsProvider>
  </AppointmentCreateModalProvider>
);

/* -------------------------------------------------------------------------- */
/*                                main logic                                  */
/* -------------------------------------------------------------------------- */

function CalendarViewInner() {
  const { t, i18n } = useTranslation();
  const luxonLocale = useMemo(() => getLuxonLocale(i18n.language), [i18n.language]);
  /* ---------- context ---------- */
  const { currentDate } = useCalendar();
  const { selectedStaffId } = useCalendarData();
  const createModal = useAppointmentCreateModal();

  // Mobile pager state (show up to 2 staff columns per page)
  const isMobile = useIsMobile();
  const [page, setPage] = React.useState(0);            // all-staff (masters) pager — mobile
  const [desktopPage, setDesktopPage] = React.useState(0);
  const [dayPage, setDayPage] = React.useState(0);      // NEW: single-staff week pager — mobile

  /* ---------- render modal ---------- */
  // Ensure the modal is rendered within the provider scope

  /* ---------- derived ---------- */
  const isoDate = useMemo(
    () => currentDate.toISODate(),
    [currentDate]
  );
  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const weekStart = useMemo(() => getWeekStart(isoDate), [isoDate]);
  const weekDates2 = useMemo(() => getWeekDates2(weekStart), [weekStart]);
  const policy = useAccessPolicy();
  const effectiveStaffId = policy.timetable.forcedStaffId ?? selectedStaffId;
  const earliest = useMemo(() => earliestAllowedDate(policy), [policy]);
  const weekDates2Safe = useMemo(
    () => weekDates2.filter((d) => DateTime.fromISO(d).startOf("day") >= earliest),
    [weekDates2, earliest]
  );

  /* ---------- data fetching ---------- */
  // For single-staff week view
  const weekAppointmentsQuery = effectiveStaffId
    ? useWeekAppointmentsForStaff(effectiveStaffId, weekDates2)
    : useWeekAppointments(weekStart);
  const weekScheduleQuery = useWeekSchedule(weekStart);
  // Busy time held outside Boona (a master's Google Calendar, calendar feed, Booksy): the same
  // week, fetched beside the appointments. Never waited on — a column without data has no bands.
  const externalBusyQuery = useExternalBusyWeek(weekStart);
  const busyByColumn = useMemo(() => groupBusyBlocks(externalBusyQuery.data), [externalBusyQuery.data]);



  // For all-staff day view
  const { data: daySpecialists, isLoading: isSpecialistsLoading, error: specialistsError } = useSpecialistsForDate(isoDate);
  // Reset pager when date or specialists list changes
  React.useEffect(() => {
    setPage(0);
  }, [isoDate, daySpecialists?.length]);
  // Reset single-staff (week) pager on week change or when switching into/out of forced mode
  // For limited accounts on mobile, start on the page containing today
  React.useEffect(() => {
    if (isMobile && effectiveStaffId) {
      // weekday: 1 = Monday ... 7 = Sunday
      // dayPage 0 = Mon-Tue, 1 = Wed-Thu, 2 = Fri-Sat, 3 = Sun
      const todayPage = Math.floor((currentDate.weekday - 1) / 2);
      setDayPage(todayPage);
    } else {
      setDayPage(0);
    }
  }, [weekStart, effectiveStaffId, isMobile]);

  /* ---------- ALWAYS-called hook (fixes the error) ---------- */
  const staffRegistry = useMemo(() => getStaffRegistry(), []);

  /* ---------- dynamic grid start / end hour ---------- */
  // For all-staff day view: compute from daySpecialists
  // For single-staff week view: compute from all masters scheduled on the date
  const { dayStartHour, dayEndHour } = useMemo(() => {
    let pool: any[];
    if (effectiveStaffId) {
      // single-staff week view — use ALL masters on this date for bounds
      pool = staffRegistry.getScheduleByDate(isoDate) as any[];
    } else {
      pool = (daySpecialists ?? []) as any[];
    }

    let startH = getMinStartHour(pool);
    let endH = getMaxEndHour(pool);

    // Also extend bounds to cover any appointments that fall outside schedule intervals
    const appsForDate = (weekAppointmentsQuery.data || {})[isoDate] ?? [];
    for (const app of appsForDate) {
      const s = typeof app.startMinOfDay === "number" ? app.startMinOfDay : undefined;
      const d = typeof app.durationMin === "number" ? app.durationMin : 0;
      if (s !== undefined) {
        const appStartH = Math.floor(s / 60);
        const appEndH = Math.ceil((s + d) / 60);
        if (appStartH < startH) startH = appStartH;
        if (appEndH > endH) endH = appEndH;
      }
    }

    return { dayStartHour: startH, dayEndHour: endH };
  }, [effectiveStaffId, isoDate, staffRegistry, daySpecialists, weekAppointmentsQuery.data]);
  const hours = useMemo(() => buildHours(dayStartHour, dayEndHour), [dayStartHour, dayEndHour]);

  // total staff for the current day
  const totalStaff = (daySpecialists || []).length;

  // Desktop paging by DESKTOP_PAGE_SIZE, mobile paging by 2 (existing)
  const desktopPages = Math.ceil(totalStaff / DESKTOP_PAGE_SIZE);
  const canDesktopPrev = desktopPage > 0;
  const canDesktopNext = desktopPage < Math.max(0, desktopPages - 1);

  // Desktop visible slice (for grid)
  const startDesktop = desktopPage * DESKTOP_PAGE_SIZE;
  const visibleDesktopStaff = (daySpecialists || []).slice(startDesktop, startDesktop + DESKTOP_PAGE_SIZE);

  // pager math for mobile three-columns (scroll by 1 staff member at a time)
  const MOBILE_VISIBLE_STAFF = 3;
  // Total number of "pages" = how many times we can shift by 1 and still show 3 staff
  const mobileMaxPage = Math.max(0, totalStaff - MOBILE_VISIBLE_STAFF);
  const canPrev = page > 0;
  const canNext = page < mobileMaxPage;
  // NEW: pager math for 2-day slices in single-staff week (mobile)
  const dayPages = Math.ceil(weekDates.length / 2);
  const canDayPrev = dayPage > 0;
  const canDayNext = dayPage < Math.max(0, dayPages - 1);

  /* ---------- drag & drop ---------- */
  // Desktop only, and only for accounts that may open and save a record — the move is an
  // edit, so it rides on the same permission the modal does.
  const dragEnabled = !isMobile && policy.records.read;
  // Held over a pager arrow, a card turns the page, so a drag can reach staff members
  // who are not on the current one.
  const handlePageStep = useCallback(
    (direction: 1 | -1) =>
      setDesktopPage((p) => clamp(p + direction, 0, Math.max(0, desktopPages - 1))),
    [desktopPages]
  );

  /* ---------- helpers ---------- */
  const getAppsForDayHour = useCallback(
    (dateStr: string, hour: number) => {
      const appointmentsByDate = weekAppointmentsQuery.data || {};
      return (appointmentsByDate[dateStr] ?? []).filter((app: Appointment) => {
        const staffId =
          (app.staff && Number(app.staff.id)) || app.specialistId;
        if (staffId !== (effectiveStaffId as number)) return false;

        const slotStart = hour * 60;
        const slotEnd = (hour + 1) * 60;

        const parsedStart = (() => {
          if (Number.isFinite(app.startMinOfDay)) return app.startMinOfDay as number;
          const [sh, sm] = (app.startTime || "00:00").split(":").map((v) => parseInt(v, 10));
          return (isFinite(sh) ? sh : 0) * 60 + (isFinite(sm) ? sm : 0);
        })();

        const parsedDur = (() => {
          if (Number.isFinite(app.durationMin)) return app.durationMin as number;
          const [eh, em] = (app.endTime || "00:00").split(":").map((v) => parseInt(v, 10));
          const s = parsedStart;
          const e = (isFinite(eh) ? eh : 0) * 60 + (isFinite(em) ? em : 0);
          return Math.max(1, e - s);
        })();

        const appStart = parsedStart;
        const appEnd = appStart + parsedDur;

        return appStart < slotEnd && appEnd > slotStart;
      });
    },
    [weekAppointmentsQuery.data, effectiveStaffId]
  );

  const getAppsForStaffHour = useCallback(
    (staffId: number, hour: number) => {
      const appointmentsByDate = weekAppointmentsQuery.data || {};
      return (appointmentsByDate[isoDate] ?? []).filter((app: Appointment) => {
        // Normalize both IDs to numbers and log for debugging
        const appStaffId = Number((app.staff && app.staff.id) || app.specialistId);
        const match = appStaffId === staffId;
        if (!match) return false;

        const slotStart = hour * 60;
        const slotEnd = (hour + 1) * 60;

        const parsedStart = (() => {
          if (Number.isFinite(app.startMinOfDay)) return app.startMinOfDay as number;
          const [sh, sm] = (app.startTime || "00:00").split(":").map((v) => parseInt(v, 10));
          return (isFinite(sh) ? sh : 0) * 60 + (isFinite(sm) ? sm : 0);
        })();

        const parsedDur = (() => {
          if (Number.isFinite(app.durationMin)) return app.durationMin as number;
          const [eh, em] = (app.endTime || "00:00").split(":").map((v) => parseInt(v, 10));
          const s = parsedStart;
          const e = (isFinite(eh) ? eh : 0) * 60 + (isFinite(em) ? em : 0);
          return Math.max(1, e - s);
        })();

        const appStart = parsedStart;
        const appEnd = appStart + parsedDur;

        return appStart < slotEnd && appEnd > slotStart;
      });
    },
    [weekAppointmentsQuery.data, isoDate]
  );

  /* ---------------------------------------------------------------------- */
  /*                       SINGLE-STAFF WEEK (selected)                     */
  /* ---------------------------------------------------------------------- */
  if (effectiveStaffId) {
    if (weekAppointmentsQuery.isLoading || weekScheduleQuery.isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <span>{t("calendar.loading", "Loading data...")}</span>
        </div>
      );
    }

    return (
      <CalendarDragProvider enabled={dragEnabled}>
      <div className="flex-1 overflow-y-auto overflow-x-hidden calendar-grid">
        {/* weekday header (slice to 2 days on mobile) */}
        <div>
          {/* make header the positioning context so arrows are sticky too */}
          <div className="flex border-b sticky top-0 bg-white z-10 relative">
            <div className="w-8 sm:w-10 md:w-16 shrink-0" />
            <WeekdayHeader
              weekDates={isMobile ? weekDates.slice(dayPage * 2, dayPage * 2 + 2) : weekDates}
              locale={luxonLocale}
            />
            {/* Mobile-only day pager arrows */}
            {isMobile && dayPages > 1 && (
              <>
                <button
                  type="button"
                  aria-label={t("calendar.prev_days", "Previous days")}
                  onClick={() => setDayPage(p => Math.max(0, p - 1))}
                  className={`md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8  bg-white ${canDayPrev ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={t("calendar.next_days", "Next days")}
                  onClick={() => setDayPage(p => Math.min(dayPages - 1, p + 1))}
                  className={`md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8  bg-white ${canDayNext ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* grid */}
        <div className="flex min-w-0 overflow-x-hidden">
          {/* time column (pin the label here) */}
          <div className="relative">
            <TimeColumn hours={hours} />
            <CurrentTimeLabel isoDate={isoDate} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />
          </div>

          {/* staff columns (draw the horizontal line here; excludes app sidebar by design) */}
          <div className="relative flex-1 min-w-0 flex calendar-columns">
            {/* day cols (2 at a time on mobile) */}
            {(isMobile ? weekDates.slice(dayPage * 2, dayPage * 2 + 2) : weekDates).map((d) => (
              <div key={d} className={isMobile ? "basis-1/2 min-w-0" : "min-w-0 flex-1"}>
                <DayColumn
                  date={d}
                  getApps={(h) => getAppsForDayHour(d, h)}
                  busyBlocks={busyByColumn.get(busyColumnKey(effectiveStaffId, d)) ?? NO_BUSY}
                  hours={hours}
                  dayStartHour={dayStartHour}
                  dayEndHour={dayEndHour}
                />
              </div>
            ))}
            {/* Live current-time line across the staff grid only (no bleed into app sidebar) */}
            <CurrentTimeRule isoDate={isoDate} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />
          </div>
        </div>
      </div>
      </CalendarDragProvider>
    );
  }

  /* ---------------------------------------------------------------------- */
  /*                         ALL-STAFF SINGLE-DAY view                      */
  /* ---------------------------------------------------------------------- */
  if (isSpecialistsLoading || weekAppointmentsQuery.isLoading || weekScheduleQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span>{t("calendar.loading", "Loading data...")}</span>
      </div>
    );
  }
  if (specialistsError) {
    return (
      <div className="flex-1 flex items-center justify-center text-red-500">
        {t("calendar.error_loading_specialists", "Error loading specialists.")}
      </div>
    );
  }

  // Empty state: no masters assigned for this day
  if (!daySpecialists || daySpecialists.length === 0) {
    return (
      <div className="flex-1 h-full min-h-[400px] flex flex-col items-center justify-center text-center px-4">
        <AlertTriangle className="h-12 w-12 text-amber-400 mb-5" strokeWidth={1.5} />
        <p className="text-gray-500 text-base mb-2">{t("calendar.no_schedule", "No schedule set for this day")}</p>
        <button
          type="button"
          onClick={() => void goToDashboard("/dashboard/staff")}
          className="text-amber-600 hover:text-amber-700 text-base font-medium underline underline-offset-2"
        >
          {t("calendar.assign_staff", "Assign staff member")}
        </button>
      </div>
    );
  }

  // @ts-ignore
  return (
    <CalendarDragProvider enabled={dragEnabled} onPageStep={handlePageStep}>
    <div className="flex-1 overflow-y-auto overflow-x-hidden calendar-grid">
      <ModalCreateAppointment />
      {/* specialists header */}
      <div className="flex border-b sticky top-0 bg-white z-10 w-full min-w-0 relative">
        <div className="w-8 sm:w-10 md:w-16 flex-none flex items-center">
          {/* spacer for time column alignment */}
        </div>

        <div className="flex-1 min-w-0 flex w-full pr-[var(--calendar-column-gap)]">
          {isMobile ? (
            ((daySpecialists || []).slice(page, page + MOBILE_VISIBLE_STAFF)).map((s) => (
              <div key={s.id} className="basis-1/3 min-w-0">
                <EmployeeHeader specialist={s} />
              </div>
            ))
          ) : (
            // Desktop: render current page slice as flex children with equal widths
            <>
              {visibleDesktopStaff.map((s) => (
                <div key={s.id} className="min-w-0 flex-1">
                  <EmployeeHeader specialist={s} />
                </div>
              ))}
            </>
          )}
        </div>

        {/* mobile left arrow - absolute positioned to overlay */}
        {isMobile && totalStaff > MOBILE_VISIBLE_STAFF && canPrev && (
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label={t("calendar.prev_staff", "Previous specialists")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 rounded-r shadow-sm"
          >
            <ChevronLeft size={18} />
          </button>
        )}

        {/* mobile right arrow - absolute positioned to overlay */}
        {isMobile && totalStaff > MOBILE_VISIBLE_STAFF && canNext && (
          <button
            onClick={() => setPage((p) => Math.min(p + 1, mobileMaxPage))}
            aria-label={t("calendar.next_staff", "Next specialists")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 rounded-l shadow-sm"
          >
            <ChevronRight size={18} />
          </button>
        )}

        {/* desktop pager arrows */}
        {!isMobile && totalStaff > DESKTOP_PAGE_SIZE && (
          <>
            <StaffPagerArrow
              direction={-1}
              enabled={canDesktopPrev}
              label={t("calendar.prev_staff_page", "Previous staff page")}
              onClick={() => setDesktopPage(p => Math.max(0, p - 1))}
              className="left-1"
            />
            <StaffPagerArrow
              direction={1}
              enabled={canDesktopNext}
              label={t("calendar.next_staff_page", "Next staff page")}
              onClick={() => setDesktopPage(p => Math.min(desktopPages - 1, p + 1))}
              className="right-1"
            />
          </>
        )}
      </div>

      {/* grid */}
      <div className="flex min-w-0 overflow-x-hidden gap-1 pr-0 xl:pr-10">
        {/* time column (pin the label here) */}
        <div className="relative">
          <TimeColumn hours={hours} />
          <CurrentTimeLabel isoDate={isoDate} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />
        </div>

        {/* staff columns (draw the horizontal line here; excludes app sidebar by design) */}
        <div className="relative flex-1 min-w-0 flex calendar-columns">
          {/* staff cols */}
          {isMobile ? (
            ((daySpecialists || []).slice(page, page + MOBILE_VISIBLE_STAFF)).map((s) => (
              <React.Fragment key={s.id}>
                <div className="basis-1/3 min-w-0">
                  <StaffColumn
                    specialist={s}
                    date={isoDate}
                    getApps={(h) => getAppsForStaffHour(s.id, h)}
                    busyBlocks={busyByColumn.get(busyColumnKey(s.id, isoDate)) ?? NO_BUSY}
                    staffRegistry={staffRegistry}
                    createModal={createModal}
                    hours={hours}
                    dayStartHour={dayStartHour}
                    dayEndHour={dayEndHour}
                  />
                </div>
              </React.Fragment>
            ))
          ) : (
            // Desktop: equal-width columns, gap and right padding handled by CSS
            <>
              {visibleDesktopStaff.map((s) => (
                <div key={s.id} className="min-w-0 flex-1">
                  <StaffColumn
                    specialist={s}
                    date={isoDate}
                    getApps={(h) => getAppsForStaffHour(s.id, h)}
                    busyBlocks={busyByColumn.get(busyColumnKey(s.id, isoDate)) ?? NO_BUSY}
                    staffRegistry={staffRegistry}
                    createModal={createModal}
                    hours={hours}
                    dayStartHour={dayStartHour}
                    dayEndHour={dayEndHour}
                  />
                </div>
              ))}
            </>
          )}
          {/* Live current-time line across the staff grid only (no bleed into app sidebar) */}
          <CurrentTimeRule isoDate={isoDate} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />
        </div>
      </div>
    </div>
    </CalendarDragProvider>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Pure presentational sub-components                   */
/* -------------------------------------------------------------------------- */

/**
 * A staff-pager arrow that doubles as a drop zone.
 *
 * Only eight staff members fit on a page, so without this a card could never reach the
 * ninth. Holding one over an arrow turns the page and keeps the drag alive; the arrow
 * stays clickable as before.
 */
const StaffPagerArrow: React.FC<{
  direction: 1 | -1;
  enabled: boolean;
  label: string;
  onClick: () => void;
  className: string;
}> = ({ direction, enabled, label, onClick, className }) => {
  const { setNodeRef, isOver, isDragActive } = usePagerDropZone(direction);

  // While a card is in the air the arrow stays reachable even when it is at the end of
  // the range, so the drag has somewhere to hover rather than a dead edge.
  const visible = enabled || isDragActive;

  return (
    <button
      ref={setNodeRef}
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={!enabled}
      className={[
        "hidden md:flex items-center justify-center absolute top-2/4 -translate-y-1/2",
        "h-8 w-8 bg-white shadow",
        className,
        visible ? "opacity-100" : "opacity-0 pointer-events-none",
        isOver ? "ring-2 ring-amber-500" : "",
      ].join(" ")}
    >
      {direction === -1 ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );
};

const TimeColumn: React.FC<{ hours: number[] }> = ({ hours }) => (
  <div className="w-9 sm:w-10 md:w-16 shrink-0 calendar-column">
    {hours.map((h) => (
      <div key={h} className="h-[var(--slot-h)] relative calendar-cell--hour">
        <div className="absolute -top-2 left-2 text-[10px] sm:text-xs md:text-sm">{h}:00</div>
        <div className="absolute top-[calc(var(--slot-h)/2)] left-2 text-[9px] sm:text-[10px] text-gray-400">
          30
        </div>
        <div className="absolute left-0 right-0 top-[calc(var(--slot-h)/2)] calendar-cell--half" />
      </div>
    ))}
  </div>
);

/* ----------------------- appointment overlay & tiling ---------------------- */

/** An appointment paired with the salon-time geometry its card is drawn at. */
type TimedAppointment = { app: Appointment; startMin: number; durMin: number };

/** Where a column's cards sit, so the busy bands beneath can keep their labels in sight. */
const cardSpans = (items: TimedAppointment[]) =>
  items.map(({ startMin, durMin }) => ({ startMin, endMin: startMin + durMin }));

/** Space between a card and the column edge. Matches the old `inset-x-1`. */
const CARD_GUTTER_PX = 4;
/** Indent added per stacking depth — the strip left exposed on the card beneath. */
const STACK_STEP_PX = 16;
/** The deepest card in a pileup never indents below this width. */
const MIN_CARD_PX = 56;

/**
 * Draws appointment cards over an hour grid. Appointments that overlap in time are
 * stacked one over another, each indented from the one it covers, so no card is ever
 * hidden completely.
 *
 * Callers resolve each appointment's start/duration themselves, because the day and
 * staff views have different fallbacks for un-normalized data.
 */
const AppointmentsOverlay: React.FC<{
  items: TimedAppointment[];
  dayStartHour: number;
  dayEndHour: number;
  /** Staff member this column belongs to — the drop target's identity. */
  staffId?: number | null;
  staffName?: string;
  date: string;
  /** Working intervals (seconds) a dropped card has to fit inside. */
  intervals?: { start: number; end: number }[];
}> = ({ items, dayStartHour, dayEndHour, staffId, staffName, date, intervals }) => {
  const dayStartMin = dayStartHour * 60;
  const dayEndMin = dayEndHour * 60;

  // Clamp before tiling, so lanes reflect the band each card actually occupies —
  // two appointments squeezed into the same band by the day bounds still get
  // separate lanes rather than landing on top of each other.
  // The unclamped times ride along: a drag has to report where the appointment
  // really is, not where the day bounds forced it to be drawn.
  const drawn = useMemo(
    () =>
      items.map(({ app, startMin, durMin }) => {
        const clampedStart = clamp(startMin, dayStartMin, dayEndMin);
        const maxDur = Math.max(0, dayEndMin - clampedStart);
        return {
          app,
          startMin: clampedStart,
          endMin: clampedStart + Math.max(1, Math.min(durMin, maxDur)),
          sourceStartMin: startMin,
          sourceDurMin: durMin,
        };
      }),
    [items, dayStartMin, dayEndMin]
  );

  const occupied = useMemo(
    () => items.map(({ app, startMin, durMin }) => ({ id: app.id, startMin, durationMin: durMin })),
    [items]
  );

  const { columnId: dropColumnId, setNodeRef } = useDropColumn({
    staffId: Number(staffId ?? 0),
    staffName,
    date,
    intervals: intervals ?? [],
    occupied,
    dayStartMin,
    dayEndMin,
  });

  const stacking = useMemo(
    () => layoutAppointments(drawn.map(({ app, startMin, endMin }) => ({ id: app.id, startMin, endMin }))),
    [drawn]
  );

  return (
    <div ref={setNodeRef} className="calendar-overlay pointer-events-none">
      {drawn.map(({ app, startMin, endMin, sourceStartMin, sourceDurMin }) => {
        const { depth, depthCount } = stacking.get(app.id) ?? { depth: 0, depthCount: 1 };

        // Shrink the indent on deep pileups so the last card stays readable, and never
        // let it reach 0 — a distinct indent per depth is what keeps each card exposed.
        const step = `max(1px, min(${STACK_STEP_PX}px, (100% - ${2 * CARD_GUTTER_PX}px - ${MIN_CARD_PX}px) / ${Math.max(1, depthCount - 1)}))`;
        const top = ((startMin - dayStartMin) / 60) * getHourPx();
        const height = ((endMin - startMin) / 60) * getHourPx();

        return (
          <DraggableAppointment
            key={app.id}
            app={app}
            staffId={Number(staffId ?? 0)}
            date={date}
            startMin={sourceStartMin}
            durationMin={sourceDurMin}
            style={{
              top: `${top}px`,
              height: `${Math.max(1, height)}px`,
              // At depth 0 these reduce to the original full-width `inset-x-1` card.
              left: `calc(${CARD_GUTTER_PX}px + ${depth} * ${step})`,
              width: `calc(100% - ${2 * CARD_GUTTER_PX}px - ${depth} * ${step})`,
              // Must track depth: a card may only cover ones it is indented past.
              zIndex: depth,
            }}
          />
        );
      })}
      <ColumnDragGhost columnId={dropColumnId} dayStartMin={dayStartMin} />
    </div>
  );
};

/**
 * One appointment card, positioned by the overlay and draggable onto another slot.
 * Split out so each card can own a draggable hook without the overlay re-rendering.
 */
const DraggableAppointment: React.FC<{
  app: Appointment;
  staffId: number;
  date: string;
  startMin: number;
  durationMin: number;
  style: React.CSSProperties;
}> = ({ app, staffId, date, startMin, durationMin, style }) => {
  const { enabled } = useCalendarDragActions();
  const { setNodeRef, isDragging, dragProps } = useDraggableAppointment({
    appointment: app,
    staffId,
    date,
    startMin,
    durationMin,
  });

  return (
    <div
      ref={setNodeRef}
      className={[
        "absolute pointer-events-auto",
        enabled ? "cursor-grab" : "",
        // The original stays in place, faded, so the row it came from reads as "moving".
        isDragging ? "opacity-40" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      data-start-min={typeof app.startMinOfDay === "number" ? app.startMinOfDay : undefined}
      data-duration-min={typeof app.durationMin === "number" ? app.durationMin : undefined}
      {...dragProps}
    >
      <AppointmentCard appointment={app} />
    </div>
  );
};

type DayColumnProps = {
  date: string;
  getApps: (hour: number) => Appointment[];
  /** The master's busy time held outside Boona on this day. */
  busyBlocks: readonly BusyBlock[];
  hours: number[];
  dayStartHour: number;
  dayEndHour: number;
};
const DayColumn: React.FC<DayColumnProps> = ({ date, getApps, busyBlocks, hours, dayStartHour, dayEndHour }) => {
  const { selectedStaffId } = useCalendarData();
  const createModal = useAppointmentCreateModal();
  const staffRegistry = useMemo(() => getStaffRegistry(), []);
  const policy = useAccessPolicy();
  const effectiveStaffId = policy.timetable.forcedStaffId ?? selectedStaffId;
  const allowCreate = policy.records.create;

  const specialist = useMemo(() => {
    if (!effectiveStaffId) return null;
    const dailySchedule = staffRegistry.getScheduleByDate(date);
    return dailySchedule.find((s) => s.id === effectiveStaffId) || staffRegistry.getStaffById(effectiveStaffId);
  }, [effectiveStaffId, staffRegistry, date]);

  // Build a unique list of appointments for the entire day column
  const allApps: Appointment[] = useMemo(() => {
    const seen = new Set<string | number>();
    const flat: Appointment[] = [];
    hours.forEach((h) => {
      getApps(h).forEach((a) => {
        if (!seen.has(a.id)) {
          seen.add(a.id);
          flat.push(a);
        }
      });
    });
    return flat;
  }, [getApps]);

  // Normalized minutes only; ISO→salon fallback if needed.
  const timedApps: TimedAppointment[] = useMemo(() => {
    const out: TimedAppointment[] = [];
    for (const a of allApps) {
      let baseStart0 = a.startMinOfDay as number | undefined;
      let baseDur = a.durationMin as number | undefined;
      if (baseStart0 == null || baseDur == null) {
        // Safe fallback from ISO → salon tz minutes (no device tz).
        // NOTE: isoStartUtc/isoEndUtc are produced by normalizer.
        // If absent, we still refuse to parse raw "HH:mm" strings for placement.
        const maybeIsoStart = (a as any).isoStartUtc as string | undefined;
        const maybeIsoEnd = (a as any).isoEndUtc as string | undefined;
        if (maybeIsoStart) {
          baseStart0 = minutesOfDayFromIsoSalon(maybeIsoStart);
        }
        if (maybeIsoStart && maybeIsoEnd) {
          baseDur = Math.max(15, minutesOfDayFromIsoSalon(maybeIsoEnd) - baseStart0!);
        }
      }

      if (baseStart0 == null || baseDur == null) {
        // As a last resort (should not happen with normalized data),
        // log to help catch bad pipelines rather than silently misplacing.
        console.warn("[calendar] missing normalized timing:", a);
        continue;
      }

      out.push({
        app: a,
        startMin: Number.isFinite(baseStart0) ? baseStart0 : 0,
        durMin: Number.isFinite(baseDur) ? baseDur : 60,
      });
    }
    return out;
  }, [allApps]);
  const cards = useMemo(() => cardSpans(timedApps), [timedApps]);

  const intervals = specialist ? (specialist as any).intervals ?? [] : [];

  return (
    <div className="flex-1 min-w-0 relative calendar-column">
      {hours.map((h) => {
        const apps = getApps(h); // still used for availability & create behavior
        const hourStartSec = h * 3600;
        const hourEndSec = (h + 1) * 3600;
        const isAvailable = intervals.some(
          ({ start, end }: { start: number; end: number }) =>
            start < hourEndSec && end > hourStartSec
        );

        // Click handler for a 15-min block. Default duration = 60 minutes.
        const handleQuarterClick =
          isAvailable && allowCreate && effectiveStaffId && specialist
            ? (startMinutes: number) => {
              const startHM = minutesToHM(startMinutes);
              const endHM = minutesToHM(addMinutes(startMinutes, 60));
              createModal.openModal({
                staffId: effectiveStaffId,
                staffObj: specialist,
                date,
                startTime: startHM,
                endTime: endHM,
                staffRegistry,
              });
            }
            : undefined;

        return (
          <HourCell
            key={h}
            hour={h}
            isAvailable={isAvailable}
            intervals={intervals}
            appointments={apps}
            onQuarterClick={handleQuarterClick}
          />
        );
      })}

      {/* Busy time held outside Boona — under the cards, over the hour cells */}
      <BusyBlocksLayer blocks={busyBlocks} cards={cards} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />

      {/* ⬇️ Overlay layer rendering full-height appointment blocks */}
      <AppointmentsOverlay
        items={timedApps}
        dayStartHour={dayStartHour}
        dayEndHour={dayEndHour}
        staffId={effectiveStaffId}
        staffName={(specialist as any)?.name}
        date={date}
        intervals={intervals}
      />
    </div>
  );
};

type StaffColumnProps = {
  specialist: Specialist;
  date: string;
  getApps: (hour: number) => Appointment[];
  /** The master's busy time held outside Boona on this day. */
  busyBlocks: readonly BusyBlock[];
  staffRegistry: ReturnType<typeof getStaffRegistry>;
  createModal: ReturnType<typeof useAppointmentCreateModal>;
  hours: number[];
  dayStartHour: number;
  dayEndHour: number;
};
const StaffColumn: React.FC<StaffColumnProps> = ({
  specialist,
  date,
  getApps,
  busyBlocks,
  staffRegistry,
  createModal,
  hours,
  dayStartHour,
  dayEndHour,
}) => {
  const intervals = (specialist as any).intervals ?? [];
  const policy = useAccessPolicy();
  const allowCreate = policy.records.create;

  const allApps: Appointment[] = useMemo(() => {
    const seen = new Set<string | number>();
    const flat: Appointment[] = [];
    hours.forEach((h) => {
      getApps(h).forEach((a) => {
        if (!seen.has(a.id)) {
          seen.add(a.id);
          flat.push(a);
        }
      });
    });
    return flat;
  }, [getApps]);

  const timedApps: TimedAppointment[] = useMemo(
    () =>
      allApps.map((a) => {
        const baseStart0 = Number.isFinite(a.startMinOfDay)
          ? (a.startMinOfDay as number)
          : timeToMinutes(a.startTime);
        const baseDur0 = Number.isFinite(a.durationMin)
          ? (a.durationMin as number)
          : Math.max(1, timeToMinutes(a.endTime) - timeToMinutes(a.startTime));

        return {
          app: a,
          startMin: Number.isFinite(baseStart0) ? baseStart0 : 0,
          durMin: Number.isFinite(baseDur0) ? baseDur0 : 60,
        };
      }),
    [allApps]
  );
  const cards = useMemo(() => cardSpans(timedApps), [timedApps]);

  return (
    <div className="flex-1 min-w-0 relative calendar-column">
      {hours.map((h) => {
        const apps = getApps(h);
        const hourStartSec = h * 3600;
        const hourEndSec = (h + 1) * 3600;
        const isAvailable = intervals.some(
          ({ start, end }: { start: number; end: number }) =>
            start < hourEndSec && end > hourStartSec
        );

        // Click handler for a 15-min block. Default duration = 60 minutes.
        const handleQuarterClick =
          isAvailable && allowCreate
            ? (startMinutes: number) => {
              const startHM = minutesToHM(startMinutes);
              const endHM = minutesToHM(addMinutes(startMinutes, 60));
              createModal.openModal({
                staffId: specialist.id,
                staffObj: specialist,
                date,
                startTime: startHM,
                endTime: endHM,
                staffRegistry,
              });
            }
            : undefined;

        return (
          <HourCell
            key={h}
            hour={h}
            isAvailable={isAvailable}
            intervals={intervals}
            appointments={apps}
            onQuarterClick={handleQuarterClick}
          />
        );
      })}

      {/* Busy time held outside Boona — under the cards, over the hour cells */}
      <BusyBlocksLayer blocks={busyBlocks} cards={cards} dayStartHour={dayStartHour} dayEndHour={dayEndHour} />

      {/* Overlay with full-duration appointments */}
      <AppointmentsOverlay
        items={timedApps}
        dayStartHour={dayStartHour}
        dayEndHour={dayEndHour}
        staffId={specialist.id}
        staffName={specialist.name}
        date={date}
        intervals={intervals}
      />
    </div>
  );
};

type HourCellProps = {
  hour: number;
  isAvailable?: boolean;
  /** Availability intervals for the staff member [{start: seconds, end: seconds}] */
  intervals?: { start: number; end: number }[];
  appointments: Appointment[];
  /** Called with start minutes-from-00:00 of the selected 15-min block */
  onQuarterClick?: (startMinutes: number) => void;
};
const HourCell: React.FC<HourCellProps> = ({ hour, isAvailable = true, intervals = [], appointments, onQuarterClick }) => {
  // Check if a specific 15-min slot is within an availability interval
  const isSlotWithinAvailability = (slotStartMin: number) => {
    const slotStartSec = slotStartMin * 60;
    // The slot is available if its START is within any interval [start, end).
    // Using strict less-than for end ensures that a slot starting at the break boundary
    // (e.g., 16:00 when interval ends at 16:00) is NOT available.
    return intervals.some(
      ({ start, end }) => start <= slotStartSec && slotStartSec < end
    );
  };

  // Check a specific 15-min slot against all appointments in this hour
  const isSlotOccupied = (slotStartMin: number) => {
    const slotEndMin = slotStartMin + 15;

    return appointments.some((app) => {
      let start = app.startMinOfDay;
      let duration = app.durationMin;

      // Fallback to parse string times if normalized fields are missing
      if (!Number.isFinite(start) || !Number.isFinite(duration)) {
        const [sh, sm] = (app.startTime || "00:00").split(":").map(Number);
        start = sh * 60 + sm;

        const [eh, em] = (app.endTime || "00:00").split(":").map(Number);
        let end = eh * 60 + em;
        if (end < (start ?? 0)) end += 24 * 60; // handle overnight
        duration = end - (start ?? 0);
      }

      const end = (start ?? 0) + (duration ?? 0);
      return (start ?? 0) < slotEndMin && end > slotStartMin;
    });
  };

  // Build four 15-min blocks within the hour
  const quarters = [0, 15, 30, 45];
  return (
    <div
      className={`h-[var(--slot-h)] relative calendar-cell--hour ${!isAvailable && "bg-gray-100"}`.trim()}
    /* No onClick on the whole hour — clicks are handled per quarter. */
    >
      {quarters.map((min, idx) => {
        const startMinutes = hour * 60 + min;
        const label = minutesToHM(startMinutes);
        const occupied = isSlotOccupied(startMinutes);
        // Check BOTH hour-level availability AND slot-level availability within intervals
        const slotAvailable = isAvailable && isSlotWithinAvailability(startMinutes);
        const clickable = !!onQuarterClick && slotAvailable && !occupied;
        return (
          <div
            key={idx}
            role={clickable ? "button" : undefined}
            aria-label={clickable ? `Create at ${label}` : undefined}
            className={[
              "group relative h-1/4 px-1",                    // 4 equal vertical slices
              !slotAvailable ? "bg-gray-100" : "",            // grey out unavailable slots
              clickable ? "cursor-pointer hover:bg-[#e4f3ff]" : "cursor-default",
            ].join(" ")}
            onClick={
              clickable
                ? (e) => {
                  e.stopPropagation();
                  onQuarterClick!(startMinutes);
                }
                : undefined
            }
          >
            {/* Time label appears on hover */}
            <span className="pointer-events-none absolute right-1 top-0.5 text-[10px] font-medium opacity-0 group-hover:opacity-100">
              {label}
            </span>
          </div>
        );
      })}
      {/* 30-min rail across the staff column */}
      <div
        className="pointer-events-none absolute left-0 right-0 top-[calc(var(--slot-h)/2)] calendar-cell--half"
      />
    </div>
  );
}

/* ------------------------ shared geometry & ticker ------------------------- */
function useNowLineGeometry(isoDate: string, dayStartHour: number = DEFAULT_START_HOUR, dayEndHour: number = DEFAULT_END_HOUR) {
  // Force re-render on the minute and on resize (to pick up new --slot-h)
  const [, force] = React.useReducer((x) => x + 1, 0);
  const intervalRef = React.useRef<number | null>(null);
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const tick = () => force();
    const now = salonNow(); // Luxon DateTime (salon zone) per time-handling.md
    const msToNextMinute = (60 - now.second) * 1000;
    timeoutRef.current = window.setTimeout(() => {
      tick();
      intervalRef.current = window.setInterval(tick, 60_000);
    }, msToNextMinute);
    const onResize = () => tick();
    window.addEventListener("resize", onResize);
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [isoDate]);

  const now = salonNow();
  if (now.toISODate() !== isoDate) return null; // only for "today" (salon TZ)

  const dayStartMin = dayStartHour * 60;
  const dayEndMin = dayEndHour * 60;
  const nowMin = now.hour * 60 + now.minute;
  if (nowMin < dayStartMin || nowMin > dayEndMin) return null; // out of bounds

  const topPx = ((nowMin - dayStartMin) / 60) * getHourPx();
  const label = fmtSalonHM(now).replace(":", "."); // e.g. 11.00
  return { topPx, label };
}

/* ---------------------------- horizontal rule ----------------------------- */
type CurrentTimeRuleProps = { isoDate: string; dayStartHour: number; dayEndHour: number };
const CurrentTimeRule: React.FC<CurrentTimeRuleProps> = ({ isoDate, dayStartHour, dayEndHour }) => {
  const geom = useNowLineGeometry(isoDate, dayStartHour, dayEndHour);
  if (!geom) return null;
  const { topPx } = geom;
  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-30"
      style={{ top: `${topPx + 8}px` }}
      aria-hidden
    >
      <div className="h-0.5 w-full bg-red-500" />
    </div>
  );
};

/* ------------------------------- time label ------------------------------- */
type CurrentTimeLabelProps = { isoDate: string; dayStartHour: number; dayEndHour: number };
const CurrentTimeLabel: React.FC<CurrentTimeLabelProps> = ({ isoDate, dayStartHour, dayEndHour }) => {
  const geom = useNowLineGeometry(isoDate, dayStartHour, dayEndHour);
  if (!geom) return null;
  const { topPx, label } = geom;
  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-40"
      style={{ top: `${topPx}px` }}
      aria-hidden
    >
      <div className="flex">
        <span className="ml-1 text-[12px] leading-none px-1.5 py-0.5 rounded bg-black text-white">
          {label}
        </span>
      </div>
    </div>
  );
};
